import { API_URL } from "env";
import { mixLogOut } from "../services/mixin";

/**
 * API chăm sóc khách hàng sau dịch vụ (trang /care-list).
 *  - getCompletedAppointments: GET  /api/v1/be/appointments/completed
 *      Danh sách lịch hẹn đã thực hiện (rút gọn): search, is_cared, page, limit, branch_id.
 *  - updateAppointmentCare:    POST /api/v1/be/appointments/{id}/care
 *      Cập nhật ghi chú chăm sóc + 4 điểm khảo sát (1-5 sao).
 *  - getAppointmentPhotos:     GET  /api/v1/be/appointments/{id}/photos
 *  - uploadAppointmentPhotos:  POST /api/v1/be/appointments/{id}/photos   (multipart photos[], ≤10MB/ảnh)
 *  - deleteAppointmentPhoto:   DELETE /api/v1/be/appointments/photos/{photo_id}
 */

export interface CareSurveyRatings {
    quality: number;
    staff_attitude: number;
    doctor_ktv: number;
    facilities: number;
}

export interface CareSurveyData {
    care_note: string;
    ratings: CareSurveyRatings;
}

export interface CompletedAppointment {
    id: number;
    code: string;
    customer_name: string;
    customer_phone: string;
    scheduled_at: string | null;
    action_staff_id: number | null;
    action_staff_name: string | null;
    is_cared: boolean;
    care_status_text: string;
    cared_at: string | null;
    cared_by_staff_id: number | null;
    cared_by_staff_name: string | null;
    survey_data: CareSurveyData | null;
    services: { id: number; name: string }[];
}

export interface AppointmentPhoto {
    id: number;
    image: string;
    absolute_image_url: string;
    title: string | null;
    type: string | null;
    note: string | null;
    created_at?: string;
}

const authHeaders = () => ({
    Accept: "application/json",
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

export async function getCompletedAppointments(params: {
    search?: string;
    is_cared?: number;
    page?: number;
    limit?: number;
    branch_id?: number;
} = {}): Promise<{ status: boolean; items: CompletedAppointment[]; total: number; msg?: string }> {
    try {
        const q = new URLSearchParams();
        if (params.search) q.append("search", params.search);
        if (params.is_cared !== undefined) q.append("is_cared", String(params.is_cared));
        if (params.page) q.append("page", String(params.page));
        if (params.limit) q.append("limit", String(params.limit));
        if (params.branch_id !== undefined) q.append("branch_id", String(params.branch_id));
        const res = await fetch(`${API_URL}/api/v1/be/appointments/completed?${q.toString()}`, {
            headers: authHeaders(),
        });
        if (!res.ok) {
            if (res.status === 401) { mixLogOut(); }
            throw new Error(`HTTP ${res.status}`);
        }
        const json = await res.json();
        const d = json.data || {};
        return { status: !!json.status, items: d.items || [], total: Number(d.total || 0), msg: json.msg };
    } catch (e: any) {
        return { status: false, items: [], total: 0, msg: e?.message };
    }
}

export async function updateAppointmentCare(
    appointmentId: number,
    body: { care_note: string; ratings: CareSurveyRatings; cared_by_staff_id?: number }
): Promise<{ status: boolean; data?: any; msg?: string }> {
    try {
        const res = await fetch(`${API_URL}/api/v1/be/appointments/${appointmentId}/care`, {
            method: "POST",
            headers: { ...authHeaders(), "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
        if (res.status === 401) { mixLogOut(); }
        const json = await res.json().catch(() => ({}));
        return { status: !!json.status, data: json.data, msg: json.msg };
    } catch (e: any) {
        return { status: false, msg: e?.message };
    }
}

/**
 * Khảo sát công khai — POST /api/v1/public/appointments/{id}/care
 *
 * Dùng cho trang khách hàng tự đánh giá, KHÔNG gửi token và KHÔNG gọi mixLogOut
 * (khách chưa đăng nhập, lỡ đá sang /login là hỏng luồng).
 *
 * Lưu ý về response: lỗi validate vẫn trả HTTP 200 kèm status=false, nên không
 * được dựa vào res.ok. Trường `error` có 3 dạng tuỳ tình huống:
 *   - object  {"care_note": "...", "ratings.quality": "..."}  -> lỗi validate
 *   - mảng    ["Không tìm thấy lịch hẹn."]                    -> kèm HTTP 404
 *   - chuỗi   "Lịch hẹn này đã được thực hiện khảo sát..."     -> đã đánh giá rồi
 */
export type PublicCareErrorType = "validation" | "already_cared" | "not_found" | "network";

export interface PublicCareResult {
    status: boolean;
    data?: any;
    errorType?: PublicCareErrorType;
    /** Thông báo lỗi theo từng trường, key giữ nguyên dạng API trả (vd "ratings.quality") */
    fieldErrors?: Record<string, string>;
    msg?: string;
}

export async function submitPublicAppointmentCare(
    appointmentId: number | string,
    body: { care_note: string; ratings: CareSurveyRatings }
): Promise<PublicCareResult> {
    try {
        const res = await fetch(`${API_URL}/api/v1/public/appointments/${appointmentId}/care`, {
            method: "POST",
            headers: { Accept: "application/json", "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
        const json = await res.json().catch(() => ({}));

        if (json?.status) {
            return { status: true, data: json.data, msg: json.msg };
        }

        const err = json?.error;
        if (res.status === 404) {
            return { status: false, errorType: "not_found", msg: firstMessage(err) || "Không tìm thấy lịch hẹn." };
        }
        if (err && typeof err === "object" && !Array.isArray(err)) {
            const fieldErrors: Record<string, string> = {};
            Object.entries(err).forEach(([k, v]) => {
                fieldErrors[k] = Array.isArray(v) ? String(v[0]) : String(v);
            });
            return { status: false, errorType: "validation", fieldErrors, msg: firstMessage(err) };
        }
        return { status: false, errorType: "already_cared", msg: firstMessage(err) || json?.msg };
    } catch (e: any) {
        return { status: false, errorType: "network", msg: e?.message };
    }
}

/** Lấy câu thông báo đầu tiên từ `error` dù nó là chuỗi, mảng hay object */
function firstMessage(err: any): string | undefined {
    if (!err) return undefined;
    if (typeof err === "string") return err;
    if (Array.isArray(err)) return err.length ? String(err[0]) : undefined;
    if (typeof err === "object") {
        const first = Object.values(err)[0];
        if (Array.isArray(first)) return first.length ? String(first[0]) : undefined;
        if (first !== undefined) return String(first);
    }
    return undefined;
}

export async function getAppointmentPhotos(
    appointmentId: number,
    params: { type?: string; page?: number; limit?: number } = {}
): Promise<{ status: boolean; items: AppointmentPhoto[]; total: number }> {
    try {
        const q = new URLSearchParams();
        if (params.type) q.append("type", params.type);
        if (params.page) q.append("page", String(params.page));
        if (params.limit) q.append("limit", String(params.limit));
        const res = await fetch(`${API_URL}/api/v1/be/appointments/${appointmentId}/photos?${q.toString()}`, {
            headers: authHeaders(),
        });
        if (!res.ok) {
            if (res.status === 401) { mixLogOut(); }
            throw new Error(`HTTP ${res.status}`);
        }
        const json = await res.json();
        const d = json.data || {};
        const items = Array.isArray(d) ? d : (d.items || []);
        return { status: !!json.status, items, total: Number(d.total ?? items.length) };
    } catch (e: any) {
        return { status: false, items: [], total: 0 };
    }
}

export async function uploadAppointmentPhotos(
    appointmentId: number,
    files: File[],
    extra: { type?: string; title?: string; note?: string } = {}
): Promise<{ status: boolean; data?: AppointmentPhoto[]; msg?: string }> {
    try {
        const fd = new FormData();
        files.forEach((f) => fd.append("photos[]", f));
        if (extra.type) fd.append("type", extra.type);
        if (extra.title) fd.append("title", extra.title);
        if (extra.note) fd.append("note", extra.note);
        const res = await fetch(`${API_URL}/api/v1/be/appointments/${appointmentId}/photos`, {
            method: "POST",
            headers: authHeaders(),
            body: fd,
        });
        if (res.status === 401) { mixLogOut(); }
        const json = await res.json().catch(() => ({}));
        return { status: !!json.status, data: json.data, msg: json.msg };
    } catch (e: any) {
        return { status: false, msg: e?.message };
    }
}

/**
 * Hành trình chăm sóc lịch hẹn (care logs).
 *  - GET    /api/v1/be/appointments/{appointment_id}/care-logs        (mới nhất trước)
 *  - POST   /api/v1/be/appointments/{appointment_id}/care-logs        {content, type}
 *  - PUT    /api/v1/be/appointments/{appointment_id}/care-logs/{id}   {content?, type?}
 *  - DELETE /api/v1/be/appointments/{appointment_id}/care-logs/{id}   (xóa mềm)
 * type: 1=Gọi điện, 2=Nhắn tin, 3=Gặp trực tiếp, 4=Email, 5=Khác
 */

export interface AppointmentCareLog {
    id: number;
    appointment_id: number;
    content: string;
    type: number;
    type_label: string;
    created_by_id: number;
    created_by_name: string;
    created_at: string;
}

export const CARE_LOG_TYPES = [
    { value: 1, label: "Gọi điện" },
    { value: 2, label: "Nhắn tin" },
    { value: 3, label: "Gặp trực tiếp" },
    { value: 4, label: "Email" },
    { value: 5, label: "Khác" },
];

export async function getAppointmentCareLogs(
    appointmentId: number
): Promise<{ status: boolean; items: AppointmentCareLog[] }> {
    try {
        const res = await fetch(`${API_URL}/api/v1/be/appointments/${appointmentId}/care-logs`, {
            headers: authHeaders(),
        });
        if (!res.ok) {
            if (res.status === 401) { mixLogOut(); }
            throw new Error(`HTTP ${res.status}`);
        }
        const json = await res.json();
        const d = json.data;
        return { status: !!json.status, items: Array.isArray(d) ? d : (d?.items || []) };
    } catch {
        return { status: false, items: [] };
    }
}

export async function createAppointmentCareLog(
    appointmentId: number,
    body: { content: string; type: number }
): Promise<{ status: boolean; data?: AppointmentCareLog; msg?: string }> {
    try {
        const res = await fetch(`${API_URL}/api/v1/be/appointments/${appointmentId}/care-logs`, {
            method: "POST",
            headers: { ...authHeaders(), "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
        if (res.status === 401) { mixLogOut(); }
        const json = await res.json().catch(() => ({}));
        return { status: !!json.status, data: json.data, msg: json.msg };
    } catch (e: any) {
        return { status: false, msg: e?.message };
    }
}

export async function updateAppointmentCareLog(
    appointmentId: number,
    logId: number,
    body: { content?: string; type?: number }
): Promise<{ status: boolean; data?: AppointmentCareLog; msg?: string }> {
    try {
        const res = await fetch(`${API_URL}/api/v1/be/appointments/${appointmentId}/care-logs/${logId}`, {
            method: "PUT",
            headers: { ...authHeaders(), "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
        if (res.status === 401) { mixLogOut(); }
        const json = await res.json().catch(() => ({}));
        return { status: !!json.status, data: json.data, msg: json.msg };
    } catch (e: any) {
        return { status: false, msg: e?.message };
    }
}

export async function deleteAppointmentCareLog(
    appointmentId: number,
    logId: number
): Promise<{ status: boolean; msg?: string }> {
    try {
        const res = await fetch(`${API_URL}/api/v1/be/appointments/${appointmentId}/care-logs/${logId}`, {
            method: "DELETE",
            headers: authHeaders(),
        });
        if (res.status === 401) { mixLogOut(); }
        const json = await res.json().catch(() => ({}));
        return { status: !!json.status, msg: json.msg };
    } catch (e: any) {
        return { status: false, msg: e?.message };
    }
}

export async function deleteAppointmentPhoto(
    photoId: number
): Promise<{ status: boolean; msg?: string }> {
    try {
        const res = await fetch(`${API_URL}/api/v1/be/appointments/photos/${photoId}`, {
            method: "DELETE",
            headers: authHeaders(),
        });
        if (res.status === 401) { mixLogOut(); }
        const json = await res.json().catch(() => ({}));
        return { status: !!json.status, msg: json.msg };
    } catch (e: any) {
        return { status: false, msg: e?.message };
    }
}
